import {
  HubspotAssociatedCompany,
  IAccountRespSchema,
} from '@monetize/types/app';
import { FC, useState } from 'react';
import { useGetHubspotLinkedAccounts } from '../../../../../api/crmService';
import { AccountSelect } from '../../../../../components/Account/AccountSelect';
import { MBox, MButton } from '../../../../../components/Monetize';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';

interface HubspotLinkedAccountSelectProps {
  opportunityId: string;
  value?: string;
  disabledIds?: string[];
  /** Called when user selects a HubSpot-associated company */
  onSelectHubspotCompany: (company: HubspotAssociatedCompany | null) => void;
  /** Called when user selects an account from the standard "show all" search */
  onSelectStandardAccount: (account: IAccountRespSchema) => void;
  onClearAccountValue?: () => void;
}

/**
 * Displays accounts associated with the HubSpot deal for the given opportunityId.
 * Includes a "Show All Accounts" toggle to fall back to the standard account search.
 */
export const HubspotLinkedAccountSelect: FC<
  HubspotLinkedAccountSelectProps
> = ({
  opportunityId,
  value,
  disabledIds,
  onSelectHubspotCompany,
  onSelectStandardAccount,
  onClearAccountValue,
}) => {
  const [showAllAccounts, setShowAllAccounts] = useState(false);
  const [selectedHubSpotId, setSelectedHubSpotId] = useState<string>('');

  const { data, isLoading } = useGetHubspotLinkedAccounts(opportunityId);

  const companies = data?.associatedCompanies ?? [];

  const items = companies.map((company) => ({
    id: company.hubSpotObjectId,
    label: company.name,
    company,
  }));

  const disabledItemIds = disabledIds
    ? companies
        .filter(
          (c) =>
            c.monetizeAccountId && disabledIds.includes(c.monetizeAccountId),
        )
        .map((c) => c.hubSpotObjectId)
    : [];

  const handleToggleShowAll = () => {
    setShowAllAccounts((prev) => !prev);
    setSelectedHubSpotId('');
    onSelectHubspotCompany(null);
    onClearAccountValue?.();
  };

  if (showAllAccounts) {
    return (
      <MBox w="full">
        <AccountSelect
          value={value}
          disabledIds={disabledIds}
          onClearAccountValue={onClearAccountValue}
          onChange={(account) => {
            onSelectStandardAccount(account as IAccountRespSchema);
          }}
        />
        <MButton variant="link" size="sm" mt="2" onClick={handleToggleShowAll}>
          Show Deal Accounts
        </MButton>
      </MBox>
    );
  }

  return (
    <MBox w="full">
      <MCustomSelect
        loading={isLoading}
        items={items}
        itemTitle="label"
        itemValue="id"
        value={selectedHubSpotId}
        clearable
        onClear={() => {
          setSelectedHubSpotId('');
          onSelectHubspotCompany(null);
          onClearAccountValue?.();
        }}
        disabledItems={disabledItemIds}
        onChange={(selectedId) => {
          const id = selectedId as string;
          const company = companies.find((c) => c.hubSpotObjectId === id);
          setSelectedHubSpotId(id);
          onSelectHubspotCompany(company ?? null);
        }}
        placeholder="Select a deal account"
      />
      <MButton variant="link" size="sm" onClick={handleToggleShowAll} mt="2">
        Show All Accounts
      </MButton>
    </MBox>
  );
};
